import type { FirstDraftsResponse } from '$lib/apis/content-factory/onboarding';
import type { Onboarding1FlowState } from '$lib/components/content-factory/onboarding/onboarding1Flow';

/** A server-owned request for product facts is an input checkpoint, not a retry. */
export type OnboardingFirstResultClarification = {
	version: 1;
	stateId: string;
	status: 'pending' | 'answered';
	question: string;
	inputKind: 'business_fact';
	answer: string;
};

export const readOnboardingFirstResultClarification = (
	value: unknown,
	stateId: string
): OnboardingFirstResultClarification | null => {
	if (!value || typeof value !== 'object') return null;
	const candidate = value as Partial<OnboardingFirstResultClarification>;
	if (
		candidate.version !== 1 ||
		candidate.stateId !== stateId ||
		!stateId ||
		!['pending', 'answered'].includes(candidate.status ?? '') ||
		candidate.inputKind !== 'business_fact' ||
		!String(candidate.question ?? '').trim()
	)
		return null;
	return {
		version: 1,
		stateId,
		status: candidate.status!,
		question: String(candidate.question).trim(),
		inputKind: 'business_fact',
		answer: String(candidate.answer ?? '').trim()
	};
};

export const projectOnboardingFirstResultClarification = (
	result: FirstDraftsResponse
): OnboardingFirstResultClarification | null => {
	const question = String(
		result.clarification_question ?? result.preview?.clarification_question ?? ''
	).trim();
	if (
		result.clarification_required !== true ||
		result.clarification_input_kind !== 'business_fact' ||
		!question ||
		!result.state_id
	)
		return null;
	return {
		version: 1,
		stateId: result.state_id,
		status: 'pending',
		question,
		inputKind: 'business_fact',
		answer: ''
	};
};

export const clarificationFromOnboardingResultError = (error: unknown, stateId: string) =>
	readOnboardingFirstResultClarification(
		error && typeof error === 'object'
			? (error as { clarification?: unknown }).clarification
			: null,
		stateId
	);

export const hasPendingOnboardingFirstResultClarification = (
	flow: Pick<Onboarding1FlowState, 'stateId' | 'stage' | 'firstResultClarification'>
): boolean =>
	flow.stage === 'awaiting_task' &&
	readOnboardingFirstResultClarification(flow.firstResultClarification, flow.stateId)?.status ===
		'pending';

export const answerOnboardingFirstResultClarification = (
	flow: Onboarding1FlowState,
	answer: string
): Onboarding1FlowState | null => {
	const pending = readOnboardingFirstResultClarification(
		flow.firstResultClarification,
		flow.stateId
	);
	if (!hasPendingOnboardingFirstResultClarification(flow) || !pending || !answer.trim())
		return null;
	return {
		...flow,
		stage: 'generating_result',
		firstResultClarification: { ...pending, status: 'answered', answer: answer.trim() }
	};
};
